'use client'

import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, useRef } from 'react'
import { useCookieConsent } from '@/components/cookies/cookie-consent-context'

export function CSPostHogProvider({ children }: { children: React.ReactNode }) {
  const { consent, showBanner } = useCookieConsent()
  const initializedRef = useRef(false)

  // Initialize PostHog only when analytics consent is given
  // This is a valid pattern for conditional initialization based on user consent
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'

    // Only initialize PostHog if:
    // 1. Analytics consent is given
    // 2. User has made a decision (banner not showing)
    // 3. PostHog key is available
    if (consent.analytics && !showBanner && posthogKey && !initializedRef.current) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        person_profiles: 'always',
        capture_pageview: false
      })
      initializedRef.current = true
    }

    // If consent is withdrawn, opt out of tracking
    if (!consent.analytics && initializedRef.current) {
      posthog.opt_out_capturing()
    }

    // If consent is re-given after being withdrawn, opt back in
    if (consent.analytics && !initializedRef.current && posthogKey) {
      posthog.opt_in_capturing()
      initializedRef.current = true
    }
  }, [consent.analytics, showBanner])

  // Still wrap in PostHogProvider for consistency, but it won't capture if not initialized
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}